/**
 * Pinia-стор глобального поиска топбара (DEV-303).
 *
 * Setup-стиль (как clients.store.ts). Дебаунс ручной через setTimeout
 * (конвенция проекта — см. DealsFilters.vue), устаревшие запросы отменяются
 * AbortController. API дёргается только через действия стора.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { searchApi } from '../api/search.api'
import type { SearchGroup, SearchHit } from '../types/search.types'

/** Минимальная длина запроса (короче — не дёргаем API). */
const MIN_QUERY_LENGTH = 2
/** Задержка дебаунса набора, мс. */
const DEBOUNCE_MS = 300
/** Лимит результатов на тип сущности (топбар = подсказки). */
const LIMIT_PER_TYPE = 5
/** Ключ и лимит для «недавнего» (localStorage). */
const RECENT_KEY = 'globalSearch.recent'
const RECENT_LIMIT = 5

const sameHit = (a: SearchHit, b: SearchHit) => a.type === b.type && a.entityId === b.entityId

const loadRecent = (): SearchHit[] => {
  try {
    const raw = localStorage.getItem(RECENT_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []

    return Array.isArray(parsed) ? (parsed as SearchHit[]) : []
  } catch {
    return []
  }
}

export const useGlobalSearchStore = defineStore('globalSearch', () => {
  const query = ref('')
  const groups = ref<SearchGroup[]>([])
  const total = ref(0)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const recent = ref<SearchHit[]>(loadRecent())

  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  let controller: AbortController | null = null

  const hasResults = computed(() => total.value > 0)
  const isQueryTooShort = computed(() => {
    const len = query.value.trim().length
    return len > 0 && len < MIN_QUERY_LENGTH
  })

  const resetResults = () => {
    groups.value = []
    total.value = 0
    error.value = null
  }

  /** Немедленный запрос (минуя дебаунс). */
  const runSearch = async (raw: string) => {
    const q = raw.trim()
    if (q.length < MIN_QUERY_LENGTH) {
      isLoading.value = false
      resetResults()
      return
    }

    // Отменяем предыдущий запрос — актуален только последний ввод.
    controller?.abort()
    const myController = new AbortController()
    controller = myController

    isLoading.value = true
    error.value = null
    try {
      const res = await searchApi.globalSearch(
        { query: q, limitPerType: LIMIT_PER_TYPE },
        myController.signal,
      )
      if (myController.signal.aborted) return
      groups.value = res.groups ?? []
      total.value = res.total ?? 0
    } catch (err: unknown) {
      if (myController.signal.aborted) return // отменён новым вводом — это не ошибка
      error.value = err instanceof Error ? err.message : 'Search failed'
      console.error('Global search failed:', err)
      groups.value = []
      total.value = 0
    } finally {
      // Гасим лоадер только если это всё ещё актуальный запрос.
      if (controller === myController) isLoading.value = false
    }
  }

  /** Ввод из инпута: обновляет запрос и запускает дебаунс-поиск. */
  const search = (value: string) => {
    query.value = value
    if (debounceTimer) clearTimeout(debounceTimer)
    // Лоадер включаем сразу (до дебаунса) — чтобы в дропдауне на время ожидания
    // не мелькало «ничего не найдено» между нажатиями.
    if (value.trim().length >= MIN_QUERY_LENGTH) {
      isLoading.value = true
    }
    debounceTimer = setTimeout(() => {
      void runSearch(value)
    }, DEBOUNCE_MS)
  }

  /** Полный сброс (при закрытии оверлея). */
  const clear = () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
    controller?.abort()
    controller = null
    query.value = ''
    isLoading.value = false
    resetResults()
  }

  const persistRecent = () => {
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(recent.value))
    } catch {
      /* localStorage недоступен — недавнее просто не сохранится */
    }
  }

  /** Запомнить выбранный результат в «недавнем» (вверх списка, без дублей). */
  const addRecent = (hit: SearchHit) => {
    recent.value = [hit, ...recent.value.filter((h) => !sameHit(h, hit))].slice(0, RECENT_LIMIT)
    persistRecent()
  }

  /** Убрать элемент из «недавнего». */
  const removeRecent = (hit: SearchHit) => {
    recent.value = recent.value.filter((h) => !sameHit(h, hit))
    persistRecent()
  }

  return {
    query,
    groups,
    total,
    isLoading,
    error,
    hasResults,
    isQueryTooShort,
    recent,
    search,
    runSearch,
    clear,
    addRecent,
    removeRecent,
  }
})
